import { CONSTANTS } from './../../Constants/Constant/index'
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PARAMETER_MANAGEMENT_CRUD, WIDGET_MANAGEMENT_CRUD } from '../../Constants/ApiDefinations'
import mainApiService from '../../Services'
import {
  IDataEngineCategorySlice,
  IDataEngineCategoryInstanceState,
} from '../../Models/DataCategoryUtils'

const initialState: IDataEngineCategorySlice = {
  widgetsById: {},
}

const initialDataEngineCategoryInstanceState: IDataEngineCategoryInstanceState = {
  fetchedAssetsList: [],
  payloadForSaveAction: {},
  copyPayloadForSaveAction: {},
  pinnedAssetIdsForSaveAction: [],
}

const ensureWidget = (state: IDataEngineCategorySlice, widgetId: string) => {
  if (!state.widgetsById) {
    state.widgetsById = {}
  }
  if (!state.widgetsById[widgetId]) {
    state.widgetsById[widgetId] = { ...initialDataEngineCategoryInstanceState }
  }
}

const dataEngineCategorySlice = createSlice({
  name: 'dataEngineCategory',
  initialState,
  reducers: {
    setFetchedAssetsList: (state, action: PayloadAction<{ widgetId: string; data: any }>) => {
      const { widgetId, data } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].fetchedAssetsList = data
    },
    clearFetchedAssetsList: (state, action: PayloadAction<{ widgetId: string }>) => {
      const { widgetId } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].fetchedAssetsList = []
    },
    setStorePayloadForSaveAction: (
      state,
      action: PayloadAction<{ widgetId: string; data: any }>,
    ) => {
      const { widgetId, data } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].payloadForSaveAction = data
    },
    setCopyStorePayloadForSaveAction: (
      state,
      action: PayloadAction<{ widgetId: string; data: any }>,
    ) => {
      const { widgetId, data } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].copyPayloadForSaveAction = data
    },
    clearPayloadForSaveAction: (state, action: PayloadAction<{ widgetId: string }>) => {
      const { widgetId } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].payloadForSaveAction = {}
      state.widgetsById[widgetId].pinnedAssetIdsForSaveAction = []
    },
    setStorePinnedAssetIdsForSaveAction: (
      state,
      action: PayloadAction<{ widgetId: string; data: any }>,
    ) => {
      const { widgetId, data } = action.payload
      ensureWidget(state, widgetId)
      state.widgetsById[widgetId].pinnedAssetIdsForSaveAction = data
    },
    resetDataEngineCategoryData: (state, action: PayloadAction<string | undefined>) => {
      if (!state.widgetsById) {
        state.widgetsById = {}
      }
      if (action.payload) {
        delete state.widgetsById[action.payload]
      } else {
        state.widgetsById = {}
      }
    },
  },
})

export const {
  setFetchedAssetsList,
  setStorePayloadForSaveAction,
  setCopyStorePayloadForSaveAction,
  clearPayloadForSaveAction,
  setStorePinnedAssetIdsForSaveAction,
  clearFetchedAssetsList,
  resetDataEngineCategoryData,
} = dataEngineCategorySlice.actions

// --- Selectors ---

export const makeSelectFetchedAssetsList = (widgetId: string) =>
  createSelector(
    [(state: any) => state.dataEngineCategory.widgetsById?.[widgetId]],
    (widgetState: IDataEngineCategoryInstanceState | undefined) =>
      widgetState?.fetchedAssetsList ?? [],
  )

export const makeSelectPayloadForSaveAction = (widgetId: string) =>
  createSelector(
    [(state: any) => state.dataEngineCategory.widgetsById?.[widgetId]],
    (widgetState: IDataEngineCategoryInstanceState | undefined) =>
      widgetState?.payloadForSaveAction ?? {},
  )

export const makeCreateCopyPayloadForSaveAction = (widgetId: string) =>
  createSelector(
    [(state: any) => state.dataEngineCategory.widgetsById?.[widgetId]],
    (widgetState: IDataEngineCategoryInstanceState | undefined) =>
      widgetState?.copyPayloadForSaveAction ?? {},
  )

export const makeSelectPinnedAssetIdsForSaveAction = (widgetId: string) =>
  createSelector(
    [(state: any) => state.dataEngineCategory.widgetsById?.[widgetId]],
    (widgetState: IDataEngineCategoryInstanceState | undefined) =>
      widgetState?.pinnedAssetIdsForSaveAction ?? [],
  )

export default dataEngineCategorySlice

export const getMastersData = createAsyncThunk(
  'dataEngineCategory/getMastersData',
  async (payload: any, { dispatch }) => {
    const requestedObj: any = {}
    if (payload?.url?.includes('system-configurations') || payload?.url?.includes('blitz-master')) {
      requestedObj['url'] = payload?.url
      requestedObj['method'] = CONSTANTS.HTTP_METHOD.GET
      requestedObj['data'] = {}
    } else {
      requestedObj['url'] = payload?.url
      requestedObj['method'] = CONSTANTS.HTTP_METHOD.POST
      requestedObj['data'] = payload?.body
    }
    const requestedData: any = requestedObj
    const data: any = await mainApiService(requestedData)
    return {
      key: payload,
      data: data,
    }
  },
)

export const getAutomatedMastersData = createAsyncThunk(
  'dataEngineCategory/getAutomatedMastersData',
  async (payload: any, { dispatch }) => {
    const requestedObj: any = {}
    if (payload?.url?.includes('country')) {
      payload.url = payload?.url + '?isDropDown=true'
    }
    requestedObj['url'] = payload?.url
    requestedObj['method'] = CONSTANTS.HTTP_METHOD.GET
    const requestedData: any = requestedObj
    const data = await mainApiService(requestedData)
    return {
      key: payload,
      data: data,
    }
  },
)

export const getParameterRepositoryDetail = createAsyncThunk(
  'dataEngineCategory/getParameterRepositoryDetail',
  async (payload: any, { dispatch }) => {
    const requestedData: any = PARAMETER_MANAGEMENT_CRUD.VIEW_PARAMETER(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)

export const submitDataEngineAutomatedFormAPICall = createAsyncThunk(
  'dataEngineCategory/submitDataEngineAutomatedFormAPICall',
  async (payload: any, { dispatch }) => {
    const requestedData: any = WIDGET_MANAGEMENT_CRUD.AUTOMATTED_ADD(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)

export const saveWidgetDataCategory = createAsyncThunk(
  'dataEngineCategory/saveWidgetDataCategory',
  async (payload: any, { dispatch }) => {
    const requestedData: any = WIDGET_MANAGEMENT_CRUD.WIDGET_AUTOMATTED_ADD(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)

export const updateWidgetDataCategory = createAsyncThunk(
  'dataEngineCategory/updateWidgetDataCategory',
  async (payload: any, { dispatch }) => {
    const requestedData: any = WIDGET_MANAGEMENT_CRUD.WIDGET_AUTOMATTED_UPDATE(payload)
    const data: any = await mainApiService(requestedData)
    return data
  },
)
